import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Zap, FileCheck2, Loader2 } from 'lucide-react';

const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_budget-gen-3/artifacts/pua56wyb_logo%20cauratech%20fondo%20oscuro.png';

export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [allowRegister, setAllowRegister] = useState(false);

  useEffect(() => {
    api.get('/auth/config')
      .then((r) => setAllowRegister(!!r.data?.allow_register))
      .catch(() => setAllowRegister(false));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Bienvenido de nuevo');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative z-10 grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute inset-0 tech-grid opacity-50" />
        <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(85,117,245,0.25),transparent_60%)]" />
        <div className="absolute -bottom-40 -right-24 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(76,201,240,0.18),transparent_60%)]" />

        <div className="relative">
          <img src={LOGO_DARK} alt="Cuaratech" className="h-16 w-auto" />
        </div>

        <div className="relative">
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
            Cotizaciones <span className="text-[hsl(var(--primary))]">inteligentes</span>,<br/>
            con verificación QR.
          </h2>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            Genera presupuestos profesionales, comparte un link público y entrega un QR de autenticidad. Diseñado para Cuaratech.
          </p>

          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-center gap-3"><Zap className="h-4 w-4 text-[hsl(var(--primary))]" /> Numeración automática y cálculo de IVA 16%</li>
            <li className="flex items-center gap-3"><FileCheck2 className="h-4 w-4 text-[hsl(var(--primary))]" /> PDF profesional con branding y QR</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" /> Verificación pública y firma criptográfica</li>
          </ul>
        </div>

        <div className="relative text-xs text-muted-foreground">© {new Date().getFullYear()} Cuaratech · RIF J-405320126</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center lg:hidden">
            <img src={LOGO_DARK} alt="Cuaratech" className="h-12 w-auto" />
          </div>
          <Card className="glass border-[var(--glass-border)] bg-[var(--glass)]">
            <CardContent className="p-7">
              <h1 className="font-display text-2xl font-semibold">Iniciar sesión</h1>
              <p className="mt-1 text-sm text-muted-foreground">Accede a tu panel de cotizaciones.</p>
              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@cuaratech.com"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary h-11 w-full"
                  disabled={busy || loading}
                  data-testid="login-form-submit-button"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Entrar'}
                </Button>
              </form>
              <p className="mt-5 text-center text-sm text-muted-foreground">
                {allowRegister ? (
                  <>
                    ¿No tienes cuenta?{' '}
                    <Link to="/register" className="font-medium text-[hsl(var(--primary))] hover:underline" data-testid="login-go-register-link">
                      Regístrate
                    </Link>
                  </>
                ) : (
                  <span className="text-xs">Acceso restringido. Solo personal autorizado de Cuaratech.</span>
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
